import { colorMappings, routeExtra } from "~/utils/routes";

export class Topography {
  constructor () {
    this.type = "FeatureCollection";
    this.features = [];
    this.properties = { summary: 'steepness' };
  }

  addFeature (coordinates, properties) {
    this.features.push({
      type: "Feature",
      properties,
      geometry: { type: "LineString", coordinates },
    });
  }

  toGeojson () {
    return { "type": "FeatureCollection", "features": this.features, "properties": this.properties };
  }

  static convertGeojsonToArray (geoJson) {
    return geoJson?.features[0]?.geometry?.coordinates || [];
  }

  static computeDistanceBetweenCoordinate (long1, lat1, long2, lat2) {
    const R = 6371000; // earth mean radius in meters
    const pi_rad = Math.PI / 180; // degrees * pi_rad = radians
    const lat_1 = lat1 * pi_rad;
    const lat_2 = lat2 * pi_rad;
    const delta_long = (long2 - long1) * pi_rad;
    return 2 * R * Math.asin(Math.sqrt(0.5 - Math.cos(lat_2 - lat_1) / 2 + Math.cos(lat_1) * Math.cos(lat_2) * (1 - Math.cos(delta_long)) / 2));
  }

  computeSlope (point, nextPoint) {
    const distance = Topography.computeDistanceBetweenCoordinate(point[0], point[1], nextPoint[0], nextPoint[1]);
    return Math.abs(((nextPoint[2] - point[2]) / distance) * 100);
  }

  defineSlopeType (slopeAsPercent) {
    if (slopeAsPercent < 5) { return 0; }
    if (slopeAsPercent < 7) { return 1; }
    if (slopeAsPercent < 10) { return 2; }
    if (slopeAsPercent < 15) { return 3; } else { return 4; }
  }

  // TODO : we should only keep generateORSJson
  static populateGeojsonWithArray (geoJson) {
    const coordinates = Topography.convertGeojsonToArray(geoJson);

    const topography = new Topography();

    let current_feature = []; // Holds points of current segment
    let previous_color = 999; // Default value to force first segment creation
    let pointCoordinates = [];
    let color = null;

    for (let i = 0; i < coordinates.length; i++) {
      const point = coordinates[i];
      let next_point = null;

      if (i !== coordinates.length - 1) {
        next_point = coordinates[i + 1];

        if (!point[2]) point[2] = 0;

        if (next_point && !next_point[2]) next_point[2] = 0;

        const elevation_rate = topography.computeSlope(point, next_point);
        color = topography.defineSlopeType(elevation_rate);

        pointCoordinates = [point[0], point[1], point[2]];

        if (i === 0) {
          current_feature.push(pointCoordinates);
        }
        if (color === previous_color) current_feature.push(pointCoordinates);
        if (current_feature.length === 1) {
          current_feature.push(pointCoordinates);
        } else {
          topography.addFeature(current_feature, { "attributeType": previous_color });
          const last_point = current_feature[current_feature.length - 1];
          current_feature = [];
          current_feature.push(last_point);
          current_feature.push(pointCoordinates);
        }
      } else {
        current_feature = [];
        current_feature.push(coordinates[coordinates.length - 2], [point[0], point[1], point[2]]);
        topography.addFeature(current_feature, { "attributeType": previous_color });
      }
      previous_color = color;
    }

    return [topography.toGeojson()];
  }
}

export const generateORSJson = (geometry, segments) => {
  const topography = new Topography();
  const coordinates = geometry.coordinates;

  const processRouteData = (coordinates) => {
    // Track segments and build metrics
    const steepnessValues = [];
    const steepnessSummary = {};
    const distanceToIndexMap = [{ distance: 0, index: 0 }];

    let previous_color = 999; // Default value to force first segment creation
    let current_segment_start_index = 0;
    let cumulativeDistance = 0;
    let totalDistance = 0;

    for (let i = 0; i < coordinates.length; i++) {
      const point = coordinates[i];
      let next_point = null;
      let color = null;

      // Build distance-to-index lookup for non-first points
      if (i > 0) {
        const prevPoint = coordinates[i - 1];
        const segmentDistance = Topography.computeDistanceBetweenCoordinate(
          prevPoint[0], prevPoint[1],
          point[0], point[1],
        );

        cumulativeDistance += segmentDistance;
        distanceToIndexMap.push({ distance: cumulativeDistance, index: i });

        // Add to total distance (for percentage calculations later)
        totalDistance += segmentDistance;

        // Track distance per steepness category (using previous point's steepness)
        if (previous_color !== 999) {
          if (!steepnessSummary[previous_color]) {
            steepnessSummary[previous_color] = { distance: 0, amount: 0 };
          }
          steepnessSummary[previous_color].distance += segmentDistance;
        }
      }

      // Process steepness for current point
      if (i !== coordinates.length - 1) {
        next_point = coordinates[i + 1];

        // Ensure elevation values exist
        if (!point[2]) point[2] = 0;
        if (next_point && !next_point[2]) next_point[2] = 0;

        // Calculate steepness
        const elevation_rate = topography.computeSlope(point, next_point);
        color = topography.defineSlopeType(elevation_rate);

        // Check if steepness category has changed
        if (color !== previous_color && previous_color !== 999) {
          // Record completed segment
          steepnessValues.push([current_segment_start_index, i, previous_color]);
          // Begin new segment
          current_segment_start_index = i;
        }
      } else {
        // Handle the last point - complete the final segment
        steepnessValues.push([current_segment_start_index, i, previous_color]);
      }

      previous_color = color;
    }

    // Calculate percentage distribution by category
    Object.keys(steepnessSummary).forEach((key) => {
      steepnessSummary[key].amount = (steepnessSummary[key].distance / totalDistance) * 100;
    });

    return {
      steepnessValues,
      steepnessSummary,
      totalDistance,
      distanceToIndexMap,
    };
  };

  const convertDistanceToIndexSegments = (
    segments,
    mappingKey,
    segmentKey,
    summaryKey,
    distanceToIndexMap,
  ) => {
    // Map text labels to numeric values
    const lookup = Object.entries(colorMappings[mappingKey]).reduce((acc, [key, { text }]) => {
      acc[text] = Number(key);
      return acc;
    }, {});

    // Transform distance-based segments to index-based segments
    const segmentData = segments[segmentKey];
    const indexBasedValues = segmentData.map(([startDistance, endDistance, label]) => [
      findClosestIndex(distanceToIndexMap, startDistance),
      findClosestIndex(distanceToIndexMap, endDistance),
      lookup[label],
    ]);

    // Format summary data
    const summaryData = Object.entries(segments[summaryKey]).map(([label, value]) => ({
      amount: value,
      value: lookup[label],
    }));

    return { values: indexBasedValues, summary: summaryData };
  };

  const findClosestIndex = (distanceMap, targetDistance) => {
    return distanceMap.reduce((closest, current) => {
      const currentDiff = Math.abs(current.distance - targetDistance);
      const closestDiff = Math.abs(closest.distance - targetDistance);
      return currentDiff < closestDiff ? current : closest;
    }).index;
  };

  const calculateBbox = (coords) => {
    return [
      Math.min(...coords.map(c => c[0])), // min lon
      Math.min(...coords.map(c => c[1])), // min lat
      Math.max(...coords.map(c => c[0])), // max lon
      Math.max(...coords.map(c => c[1])), // max lat
    ];
  };

  // Calculate steepness and build distance mapping
  const {
    steepnessValues,
    steepnessSummary,
    distanceToIndexMap,
  } = processRouteData(coordinates);

  // Format steepness data for output
  const formattedSteepnessSummary = Object.keys(steepnessSummary).map(key => ({
    value: parseInt(key),
    distance: steepnessSummary[key].distance,
    amount: steepnessSummary[key].amount,
  }));

  // Build extras object with steepness data
  const extras = {
    steepness: {
      values: steepnessValues,
      summary: formattedSteepnessSummary,
    },
  };

  // Process segment data if available
  if (segments && Object.keys(segments).length > 0) {
    if (segments.segment_route_size) {
      extras.routesize = convertDistanceToIndexSegments(
        segments,
        routeExtra.routesize,
        'segment_route_size',
        'route_size',
        distanceToIndexMap,
      );
    }

    if (segments.segment_route_foot) {
      extras.routefoot = convertDistanceToIndexSegments(
        segments,
        routeExtra.routefoot,
        'segment_route_foot',
        'route_foot',
        distanceToIndexMap,
      );
    }
  }

  // Get bbox for GeoJSON
  const bbox = calculateBbox(coordinates);

  // Assemble GeoJSON response
  return {
    type: "FeatureCollection",
    bbox,
    features: [
      {
        type: "Feature",
        bbox,
        properties: {
          extras,
        },
        geometry,
      },
    ],
  };
};
